import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getUserState, getLifebooksChild } from '../../../redux/selectors';
import {
	getAge,
	isCaseManagerUser,
	withoutCaseManagerActorIdentity,
} from '../../../helpers';
import { useCheckUserPermissions } from '../../../helpers/hooks';
import * as services from '../../../services';
import { resolveNspId } from './servicePostUtils';

const createPhotoArray = async (photos, childId) => {
	let photoArray = [];
	let promiseArray = [];
	photos.forEach((file) => {
		if (!!file?.location) {
			//string is already upload location
			photoArray.push(file.location);
		} else {
			promiseArray.push(services.getLocationAndUpload(file.file, childId));
		}
	});

	let photoLocationArray = await Promise.all(promiseArray);
	photoArray = [...photoArray, ...photoLocationArray];
	return photoArray;
};

const createVideoArray = async (videos, childId) => {
	const [video] = videos;
	if (!video) return [];
	if (video.location) return [video.location];
	return [await services.uploadVideo(video.file, childId)];
};

const useCreateMetadata = () => {
	const user = useSelector(getUserState);
	const child = useSelector(getLifebooksChild);

	return (reqBody) => {
		const { posted, metadata, postDraftId } = reqBody;
		let reqMetadata = { ...(metadata || {}) };

		if (isCaseManagerUser(user)) {
			reqMetadata = withoutCaseManagerActorIdentity(reqMetadata);
			reqMetadata.lastUpdated = new Date();
			if (!reqMetadata.created) {
				const { schoolId, houseId, house, dob } = child;
				reqMetadata = {
					...reqMetadata,
					age: getAge(dob, posted),
					created: new Date(),
				};
				const resolvedHouseId = houseId || house?.id;
				if (schoolId) reqMetadata.schoolId = schoolId;
				if (resolvedHouseId) reqMetadata.houseId = resolvedHouseId;
			}
			if (postDraftId) {
				delete reqMetadata.noteDraft;
				reqMetadata.postDraftId = postDraftId;
				reqBody.metadata = reqMetadata;
			}
			for (let key in reqMetadata) {
				if (!reqMetadata[key]) delete reqMetadata[key];
			}
			return reqMetadata;
		}

		reqMetadata.lastUpdated = new Date();
		reqMetadata.lastUpdatedBy = user.id;
		reqMetadata.lastUpdatedName = `${user.firstname} ${user.lastname.substring(
			0,
			1
		)}`;

		if (!metadata || (metadata && !metadata.created)) {
			const { schoolId, houseId, dob } = child;
			reqMetadata = {
				...reqMetadata,
				age: getAge(dob, posted),
				created: new Date(),
				createdBy: user.id,
				createdName: `${user.firstname} ${user.lastname.substring(0, 1)}`,
			};
			if (schoolId) {
				reqMetadata.schoolId = schoolId;
			}
			if (houseId) {
				reqMetadata.houseId = houseId;
			}
		}

		if (postDraftId) {
			if (reqMetadata.noteDraft) {
				delete reqMetadata.noteDraft;
			}
			reqMetadata.postDraftId = postDraftId;
			reqBody.metadata = reqMetadata;
		}

		for (let key in reqMetadata) {
			if (!reqMetadata[key]) {
				delete reqMetadata[key];
			}
		}
		return reqMetadata;
	};
};

export default function useCreateBody() {
	const { childId, postId } = useParams();
	const { includeUsers } = useCheckUserPermissions();
	const createMetaData = useCreateMetadata();

	return async (postState) => {
		const {
			category,
			title,
			body,
			relations,
			servicesCategory,
			photos,
			videos,
			nsp,
			nspId,
			change,
			postDraftId,
		} = postState;
		const resolvedNspId = resolveNspId({ nsp, nspId });

		const reqBody = {
			childId,
			approved: includeUsers('agencyuser', 'case-manager'),
			category,
			metadata: createMetaData(postState),
			title,
			body,
			relations,
		};

		if (videos?.length) {
			// a post has either photos or one video, matching mobile
			reqBody.videos = await createVideoArray(videos, childId);
			reqBody.photos = [];
		} else {
			reqBody.videos = [];
			if (photos) {
				reqBody.photos = await createPhotoArray(photos, childId);
			}
		}

		if (postDraftId) {
			reqBody.postDraftId = postDraftId;
		}

		if (resolvedNspId) {
			reqBody.nsp = true;
			reqBody.nspId = resolvedNspId;
			reqBody.servicesCategory =
				servicesCategory || nsp?.servicesCategory || null;
		}

		for (let key in change) {
			reqBody[key] = postState[key];
		}
		if (postId) {
			reqBody.postId = postId;
		}

		return reqBody;
	};
}
